import { environment } from '../../../environments/environment';
import { formatCrc } from './currency.util';
import { grossUnitPrice } from './price.utils';
import {
  CONDITION_LABELS,
  CONDITION_TONE,
  LANGUAGE_LABELS,
  PRODUCT_TYPE_LABELS,
  Product,
  ProductCondition,
  ProductImage,
  ProductType,
  ProductVariant,
  getPrepurchaseStock,
} from '../models/product.model';

const KNOWN_PRODUCT_TYPES: readonly ProductType[] = [
  'single', 'accessory', 'collectible', 'sealed', 'board-game',
];

/**
 * Precio de catálogo. Alimenta tarjeta, zoom, buscador, comparador, portada y
 * megamenú: es el sitio del que salieron los `₡24 000` de producción.
 *
 * `product.currency` NO se lee: `formatCrc` emite el símbolo. Una tienda que
 * factura solo en colones no puede tener el símbolo como dato variable de cada
 * producto — es la puerta por la que se cuela la incoherencia. El campo sigue en
 * el contrato de la API, pero ya no lo usa nadie para pintar.
 */
export function formatPrice(product: Product): string {
  return formatCrc(product.price);
}

export function formatOriginalPrice(product: Product): string {
  return formatCrc(product.originalPrice);
}

export function calcDiscountPercent(product: Product): number {
  const original = product.originalPrice;
  if (original == null || product.price == null || original <= product.price) return 0;
  return Math.round(((original - product.price) / original) * 100);
}

/**
 * Normaliza el tipo crudo de la API a la convención kebab-case del union
 * `ProductType`: el backend emite `board_game` (snake) y aquí se declara
 * `board-game`. Devuelve `null` si el valor no pertenece al enum.
 */
export function normalizeProductType(
  raw: string | null | undefined,
): ProductType | null {
  if (!raw) return null;
  const normalized = raw.toLowerCase().trim().replace(/_/g, '-');
  return (KNOWN_PRODUCT_TYPES as readonly string[]).includes(normalized)
    ? (normalized as ProductType)
    : null;
}

/**
 * `true` solo cuando el producto declara explícitamente ser una carta
 * individual. Los atributos de carta —condición, variante, rareza, mecánicas—
 * carecen de sentido en accesorios, sellados y juegos de mesa, así que un tipo
 * ausente o desconocido NO se asume single: la migración
 * `2026_07_21_120000_backfill_product_type_and_clear_non_single_quality`
 * rellenó el `product_type` de los productos legacy que sí lo eran.
 */
export function isSingleProduct(
  p: Pick<Product, 'productType' | 'product_type'>,
): boolean {
  return normalizeProductType(p.productType ?? p.product_type) === 'single';
}

/**
 * Devuelve la etiqueta en castellano del tipo de producto. Tolera tanto
 * kebab-case (`board-game`) como snake_case (`board_game`) por si la API
 * cambia de convención. Si el valor no está en el enum, devuelve el crudo
 * para no perder información en la UI.
 */
export function formatProductType(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const normalized = normalizeProductType(raw);
  return normalized ? PRODUCT_TYPE_LABELS[normalized] : raw;
}

/**
 * Traduce el código ISO de idioma a su etiqueta en castellano. Si la API
 * manda algo fuera del mapa (ej. "pt", "en-US") se devuelve el crudo para
 * no ocultar datos — la tabla muestra lo que haya.
 */
export function formatLanguage(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const normalized = raw.toLowerCase().trim();
  return LANGUAGE_LABELS[normalized] ?? raw;
}

/**
 * Etiqueta de idioma para las fichas. Los accesorios no llevan idioma (es un
 * atributo de carta), así que devolvemos null para que el template oculte la
 * fila aunque la BD traiga un `language` heredado. El resto de tipos —incluidos
 * los productos sin tipo— muestran su idioma.
 */
export function displayLanguage(
  p: Pick<Product, 'language' | 'productType' | 'product_type'>,
): string | null {
  if ((p.productType ?? p.product_type) === 'accessory') return null;
  return formatLanguage(p.language);
}

/**
 * Traduce el código de condición (`mint`, `lightly_played`, ...) a su
 * etiqueta legible en inglés (estándar TCG). Tolera valores fuera del enum
 * — devuelve el crudo para no ocultar datos.
 */
export function formatCondition(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const normalized = raw.toLowerCase().trim() as ProductCondition;
  return CONDITION_LABELS[normalized] ?? raw;
}

/**
 * Devuelve el tono semántico (`success`/`info`/`warning`/`alert`/`danger`)
 * asociado a la condición, para alimentar `<app-badge [variant]>`.
 * `null` si la condición es desconocida o vacía — el caller oculta el badge.
 */
export function getConditionTone(
  raw: string | null | undefined,
): 'success' | 'info' | 'warning' | 'alert' | 'danger' | null {
  if (!raw) return null;
  const normalized = raw.toLowerCase().trim() as ProductCondition;
  return CONDITION_TONE[normalized] ?? null;
}

/**
 * Divide el campo `subtype` por comas y limpia espacios. En One Piece la API
 * devuelve varios subtipos concatenados ("Goa Kingdom, Pirate"); en MTG suele
 * ser un único string ("Human Wizard") que cae naturalmente en un array de 1.
 * Devuelve `[]` cuando no hay valor para que los templates oculten la sección
 * vía `@if (chips.length)`.
 */
export function splitSubtype(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Combina `power` y `toughness` en el formato "P/T" que jugadores MTG esperan
 * (`5/5`). En One Piece sólo existe `power` (`2000`) y se devuelve solo. Si no
 * hay power, devuelve `null` para que el `@if` esconda el item.
 */
export function formatPowerToughness(
  power: string | null | undefined,
  toughness: string | null | undefined,
): string | null {
  if (!power) return null;
  if (toughness) return `${power}/${toughness}`;
  return power;
}

/**
 * `true` si el producto es un single Y al menos un campo de las 8 mecánicas
 * de juego tiene valor. Usado por quickview/PDP/featured para decidir si
 * montar la sección "Estadísticas de juego". Pokémon (sin mecánicas en estos
 * campos) y legacy (todos null) devuelven `false` y la sección no se renderiza.
 */
export function hasGameMechanics(product: Product): boolean {
  if (!isSingleProduct(product)) return false;
  return !!(
    product.color
    || product.cost
    || product.mana_cost
    || product.power
    || product.toughness
    || product.subtype
    || product.counter
    || product.card_attribute
  );
}

function applyTaxes(base: number, taxRate: number): number {
  // Fuente única: misma fórmula que el API (sin oferta = PVP de lista con IVA).
  return grossUnitPrice(base, taxRate, false, 0);
}

/**
 * Coerciona un valor del API a `number | undefined`. Laravel serializa columnas
 * DECIMAL como strings ("3097.00") y a veces omite campos (`undefined`); este
 * helper devuelve el número parseado o `undefined` si el valor no es finito.
 */
function toFiniteNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Entero nullable de los umbrales de Perrón Points. A diferencia de
 * `toFiniteNumber`, devuelve `null` (no `undefined`) para la ausencia, porque
 * `null` ES el valor que el API emite y que hay que preservar tal cual: en
 * `early_access_min_points` significa «hereda el global» y en
 * `early_access_threshold`, «producto no restringido» (R5). Confundirlo con
 * `0` cambiaría la semántica a «solo requiere sesión».
 */
function toNullableInt(value: unknown): number | null {
  const n = toFiniteNumber(value);
  return n === undefined ? null : Math.trunc(n);
}

/**
 * Convierte HTML rico (p. ej. la salida de Quill que guarda el backoffice
 * en `description`) a texto plano: elimina tags y decodifica las entidades
 * HTML más comunes. Usado por el quickview para truncar el preview sin
 * mostrar marcado ni cortar una etiqueta por la mitad. SSR-safe (no usa
 * `DOMParser`).
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * URL de la imagen de un producto a partir del NOMBRE del fichero que manda la API (`miniProduct()`, las líneas de
 * «Mis pedidos»): `${environment.imagesUrl}products/${name}`. Una URL absoluta se respeta; `null` o vacío → `null`,
 * y quien pinta pone su sustituto (nunca un `<img src="">`).
 */
export function productImageUrl(name: string | null | undefined): string | null {
  const value = name?.trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  return `${environment.imagesUrl}products/${value}`;
}

function isProductImage(img: unknown): img is ProductImage {
  return !!img && typeof img === 'object' && 'name' in (img as object);
}

function resolveImageUrl(apiProduct: Product): string {
  const images = apiProduct.images;
  if (images && images.length > 0) {
    const first = images[0];
    if (typeof first === 'string') return first;
    if (isProductImage(first)) return `${environment.imagesUrl}products/${first.name}`;
  }
  // Fallback para related_products / frequently_bought_together inline:
  // el endpoint /show-web-product sólo manda el filename como `image`.
  if (typeof apiProduct.image === 'string' && apiProduct.image.length > 0) {
    return /^https?:\/\//i.test(apiProduct.image)
      ? apiProduct.image
      : `${environment.imagesUrl}products/${apiProduct.image}`;
  }
  return '';
}

/**
 * Busca en `images` la que tenga `is_back === true` y construye la URL
 * completa con el mismo patrón que `resolveImageUrl`. Devuelve undefined
 * si no hay imagen marcada como reverso — en ese caso la ficha no pinta
 * el botón "Voltear".
 */
function resolveBackImageUrl(apiProduct: Product): string | undefined {
  const images = apiProduct.images;
  if (!images || images.length === 0) return undefined;
  const back = (images as unknown[]).find(
    (img) => isProductImage(img) && img.is_back === true,
  );
  if (!back || !isProductImage(back)) return undefined;
  return `${environment.imagesUrl}products/${back.name}`;
}

function resolveSecondImageUrl(apiProduct: Product): string | undefined {
  const images = apiProduct.images;
  if (!images || images.length < 2) return undefined;
  const second = images[1];
  if (typeof second === 'string') return second;
  if (isProductImage(second)) return `${environment.imagesUrl}products/${second.name}`;
  return undefined;
}

function resolveVariant(apiProduct: Product): ProductVariant {
  if (apiProduct.variant) return apiProduct.variant;
  if (apiProduct.is_foil) return 'foil';
  return 'non-foil';
}

function resolveProductType(apiProduct: Product): ProductType | undefined {
  if (apiProduct.productType) return apiProduct.productType;
  return normalizeProductType(apiProduct.product_type) ?? undefined;
}

/**
 * Adapta un Product tal como lo devuelve la API Laravel (con `sale_price`,
 * `images: ProductImage[]`, `is_foil`, etc.) al shape que la UI de mocks
 * espera (`price`, `imageUrl`, `variant`, `currency`, etc.). Rellena lo que
 * falte con defaults razonables para que las cards y modales existentes
 * funcionen sin cambios.
 */
export function toUiProduct(apiProduct: Product): Product {
  // Laravel + MySQL serializa DECIMAL como string ("3097.00") y TINYINT como
  // entero (0/1). El cliente normaliza a number/boolean acá para que las
  // comparaciones estrictas (typeof === 'number', === true) funcionen.
  const salePriceRaw = toFiniteNumber(apiProduct.sale_price);
  const taxRate = toFiniteNumber(apiProduct.taxes) ?? 0;

  // El API real envía `sale_price` (PVP de lista SIN IVA); los mocks de la UI
  // ya traen `price`/`originalPrice`/`isOnSale` resueltos y NO traen `sale_price`.
  // La presencia de `sale_price` distingue ambos casos.
  const isApiProduct = salePriceRaw != null;

  // `sale_price` con impuestos = precio de lista normal (pre-descuento). Cuando
  // el back omite `sale_price` (productos agotados en catálogo público) queda
  // undefined y los templates ocultan el bloque de precio bajo @if(!isOutOfStock()).
  const listPriceWithTaxes = isApiProduct
    ? applyTaxes(salePriceRaw!, taxRate)
    : undefined;

  // Oferta: el backend la afirma con `discount` (TINYINT 0/1 → truthy) y aporta
  // el porcentaje en `discountQty`. `Product::getOfferFlags()` espeja directamente
  // la columna `products.discount`, así que `discount` es la fuente fiable y única.
  // Sólo la tratamos como oferta real cuando hay un porcentaje válido (0 < pct < 100)
  // para no pintar un badge "Oferta" hueco sin cambio de precio.
  const discountPct = toFiniteNumber(apiProduct.discountQty) ?? 0;
  const validDiscount =
    !!apiProduct.discount && discountPct > 0 && discountPct < 100;

  let price: number | undefined;
  let originalPrice: number | null;
  let isOnSale: boolean;

  if (isApiProduct) {
    if (validDiscount && listPriceWithTaxes != null) {
      // El descuento se aplica SOBRE el PVP de lista con IVA.
      originalPrice = listPriceWithTaxes;
      // Un SOLO redondeo, espejo de Product::catalogGrossPrice() del API.
      // Redondear primero el IVA y luego la oferta (doble round) derivaba ±1 colón
      // entre la card y el carrito.
      price = grossUnitPrice(salePriceRaw!, taxRate, true, discountPct);
      isOnSale = true;
    } else {
      price = listPriceWithTaxes;
      originalPrice = null;
      isOnSale = false;
    }
  } else {
    // Mock ya resuelto por la UI: se respeta tal cual.
    price = apiProduct.price;
    originalPrice = apiProduct.originalPrice ?? null;
    isOnSale = apiProduct.isOnSale === true;
  }

  // 3B: derivar backImageUrl + hasDoubleFace desde la imagen con is_back=true.
  const backImageUrl = resolveBackImageUrl(apiProduct);

  // 3A bridge snake_case (API) → camelCase (UI). La API Laravel devuelve
  // short_description, card_text, card_type, collector_number; el resto de
  // campos (artist, hp, stage) ya coincide en ambas convenciones y viaja
  // intacto vía el spread. Los 8 campos de mecánicas de juego (color, cost,
  // mana_cost, power, toughness, subtype, counter, card_attribute) tampoco
  // necesitan bridge: la interfaz Product los declara snake_case igual que la API.
  const api = apiProduct as Product & Record<string, unknown>;

  const resolvedProductType = resolveProductType(apiProduct);
  const isSingle = resolvedProductType === 'single';

  return {
    ...apiProduct,
    id: apiProduct.id,
    name: apiProduct.name,
    game: apiProduct.game ?? apiProduct.categories?.[0]?.parent?.name ?? '',
    setName: apiProduct.setName ?? apiProduct.categories?.[0]?.name ?? '',
    setCode: apiProduct.setCode ?? '',
    price,
    originalPrice,
    currency: apiProduct.currency ?? '₡',
    imageUrl: apiProduct.imageUrl || resolveImageUrl(apiProduct),
    imageUrl2: apiProduct.imageUrl2 ?? resolveSecondImageUrl(apiProduct),
    backImageUrl: apiProduct.backImageUrl ?? backImageUrl,
    hasDoubleFace: apiProduct.hasDoubleFace ?? (backImageUrl !== undefined),
    variant: resolveVariant(apiProduct),
    productType: resolvedProductType,
    // La condición solo existe en cartas individuales. Los accesorios y
    // sellados antiguos arrastran una `quality` heredada del alta ('mint' por
    // defecto en el backoffice): se descarta aquí para que ninguna superficie
    // —card, ficha, quickview, destacado, comparador— tenga que repetir el gate.
    condition: isSingle
      ? (apiProduct.condition
        ?? (typeof apiProduct.quality === 'string' ? (apiProduct.quality as Product['condition']) : undefined))
      : undefined,
    stock: apiProduct.stock ?? 0,
    prepurchase_stock: getPrepurchaseStock(apiProduct),
    isNew: apiProduct.isNew ?? false,
    isOnSale,
    // `prepurchase-products` serializa el modelo Eloquent en crudo (sin cast bool
    // para `prepurchase`), a diferencia de `show-web-product`, que arma el array a
    // mano con `(bool)`. Sin este `!!`, MySQL entrega TINYINT 1/0 y las comparaciones
    // estrictas (`=== true`) de `isEarlyAccessProduct`/`isPrepurchaseSoldOut` fallan
    // en el listado/zoom aunque en la ficha funcionen (badge VIP D10/D14).
    isPreSale: !!(apiProduct.isPreSale ?? apiProduct.prepurchase),
    prepurchase: !!apiProduct.prepurchase,
    early_access: !!apiProduct.early_access,
    // Los dos umbrales conservan `null` a propósito: es un valor con
    // significado (R5 → «hereda el global» en el crudo, «no restringido» en el
    // resuelto) y `Number(x) || 0` lo convertiría en el estado «solo requiere
    // sesión», que es otra cosa. `toNullableInt` solo normaliza el string
    // numérico que la API entrega en algunas vías de serialización.
    early_access_min_points: toNullableInt(apiProduct.early_access_min_points),
    early_access_threshold: toNullableInt(apiProduct.early_access_threshold),
    language: apiProduct.language,
    rarity: apiProduct.rarity,
    // 3A: bridge snake_case → camelCase.
    shortDescription: apiProduct.shortDescription ?? (api['short_description'] as string | undefined),
    cardText: apiProduct.cardText ?? (api['card_text'] as string | undefined),
    cardType: apiProduct.cardType ?? (api['card_type'] as string | undefined),
    collectorNumber: apiProduct.collectorNumber ?? (api['collector_number'] as string | undefined),
    // 3C1: bridge snake_case → camelCase. `brand` y `contents` ya son idénticos
    // en ambos lados y se propagan por el spread; sólo release_date necesita bridge.
    releaseDate: apiProduct.releaseDate ?? (api['release_date'] as string | undefined),
  };
}

/**
 * Variante de `toUiProduct` para la ficha de detalle: aplica el mismo mapeo base
 * y además:
 * - Normaliza `images` a `string[]` con URLs resueltas, excluyendo la cara
 *   trasera (`is_back: true`) que se expone por separado en `backImageUrl`.
 * - Pasa `related_products` y `frequently_bought_together` por `toUiProduct`
 *   para que ProductCard los pinte sin adaptadores extra (imageUrl, price con IVA,
 *   variant, condition).
 * Mantiene el resto de campos de detalle (specs, categories, stock_alert,
 * prepurchase, max_units_per_*) vía el spread de `toUiProduct`.
 */
export function toUiProductDetail(apiProduct: Product): Product {
  const base = toUiProduct(apiProduct);
  return {
    ...base,
    images: normalizeGalleryImages(apiProduct),
    related_products: apiProduct.related_products?.map(toUiProduct) ?? base.related_products,
    frequently_bought_together:
      apiProduct.frequently_bought_together?.map(toUiProduct)
      ?? base.frequently_bought_together,
  };
}

function normalizeGalleryImages(apiProduct: Product): string[] {
  const raw = apiProduct.images;
  if (!raw || raw.length === 0) {
    return apiProduct.imageUrl ? [apiProduct.imageUrl] : [];
  }
  const urls: string[] = [];
  for (const img of raw) {
    if (typeof img === 'string') {
      urls.push(img);
    } else if (isProductImage(img) && img.is_back !== true) {
      urls.push(`${environment.imagesUrl}products/${img.name}`);
    }
  }
  return urls;
}
